import { InsulinCategory, InsulinProduct } from '../../types/domain';

const CATEGORY_LABELS: Record<InsulinCategory, string> = {
  basal: 'Insulin Basal (kerja panjang / menengah)',
  bolus: 'Insulin Bolus (kerja cepat / pendek)',
  premix: 'Insulin Premix'
};

const OTHER_VALUE = '__other__';

export type ProductSelection =
  | { kind: 'none' }
  | { kind: 'product'; id: string }
  | { kind: 'other'; name: string };

interface InsulinProductSelectProps {
  products: InsulinProduct[];
  value: ProductSelection;
  onChange: (value: ProductSelection) => void;
  id: string;
  disabled?: boolean;
}

/**
 * The SoW 3.2 catalog picker, grouped Basal / Bolus / Premix with a
 * "Lainnya (isi sendiri)" escape hatch per the spec.
 *
 * Unlike DF-09's hospital picker, a typed name here is stored as-is rather
 * than becoming a pending catalog row: insulin brands are not used to match
 * patients to anyone, so a variant spelling is cosmetic rather than
 * structural, and the curated list is fixed by the SoW rather than growing.
 */
export const InsulinProductSelect: React.FC<InsulinProductSelectProps> = ({
  products,
  value,
  onChange,
  id,
  disabled = false
}) => {
  const grouped = (['basal', 'bolus', 'premix'] as InsulinCategory[])
    .map(category => ({
      category,
      items: products.filter(p => p.category === category)
    }))
    .filter(group => group.items.length > 0);

  const selectValue =
    value.kind === 'product' ? value.id : value.kind === 'other' ? OTHER_VALUE : '';

  return (
    <>
      <select
        id={id}
        value={selectValue}
        disabled={disabled}
        onChange={event => {
          const next = event.target.value;
          if (!next) {
            onChange({ kind: 'none' });
          } else if (next === OTHER_VALUE) {
            onChange({ kind: 'other', name: '' });
          } else {
            onChange({ kind: 'product', id: next });
          }
        }}
      >
        <option value="">Pilih jenis insulin</option>
        {grouped.map(group => (
          <optgroup key={group.category} label={CATEGORY_LABELS[group.category]}>
            {group.items.map(product => (
              <option key={product.id} value={product.id}>
                {product.name}
                {product.genericName ? ` — ${product.genericName}` : ''}
              </option>
            ))}
          </optgroup>
        ))}
        <option value={OTHER_VALUE}>Lainnya (isi sendiri)</option>
      </select>

      {value.kind === 'other' ? (
        <input
          type="text"
          style={{ marginTop: 8 }}
          value={value.name}
          disabled={disabled}
          placeholder="Tulis nama insulin"
          aria-label="Nama insulin lainnya"
          onChange={event => onChange({ kind: 'other', name: event.target.value })}
        />
      ) : null}
    </>
  );
};

export default InsulinProductSelect;
